import Cookie from 'js-cookie';

import { SentryAcpAutoshipErrors as SentryErrors } from '@libs/common-next/constants';
import { getENV, logSentryError } from '@libs/common-next/utils';
import { AUTOSHIP_CACHE_KEY, OG_COOKIE } from '@libs/core';
import { isLocaleCA } from '@libs/core/utils';
import { generateOrderGrooveAuthSignature } from '@libs/order-groove/server';

export const getOgScriptUrl = () => {
    const STATIC_DOMAIN = getENV('NEXT_PUBLIC_OG_STATIC_DOMAIN');

    const MERCHANT_ID = isLocaleCA() ? getENV('NEXT_PUBLIC_OG_MERCHANT_ID_CA') : getENV('NEXT_PUBLIC_OG_MERCHANT_ID');

    return `${STATIC_DOMAIN}/${MERCHANT_ID}/msi.js`;
};

type SetOgAuthCookieParams = {
    userSid: string;
};

export const setOgAuthCookie = async ({ userSid }: SetOgAuthCookieParams): Promise<boolean> => {
    if (!userSid) {
        logSentryError(new Error('No OG userSid found'), SentryErrors.AutoshipPage.NoOgCustomerIdFound);

        return false;
    }

    if (userSid) {
        try {
            Cookie.remove(OG_COOKIE);

            const { timestamp, signature } = await generateOrderGrooveAuthSignature(userSid);

            Cookie.set(OG_COOKIE, `${userSid}|${timestamp}|${signature}`, {
                path: '/',
                expires: Date.now() + 2 * 60 * 60 * 1000,
                secure: true,
            });

            return true;
        } catch (error) {
            logSentryError(error, SentryErrors.AutoshipPage.GenerateOrdergrooveAuthSignatureFailed);

            return false;
        }
    }

    return false;
};

export const AUTOSHIP_CACHE_TTL = 2 * 60 * 60 * 1000; // 2 hours

export const readCache = () => {
    try {
        const cached = localStorage.getItem(AUTOSHIP_CACHE_KEY);
        if (!cached) return null;

        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();

        if (now - timestamp > AUTOSHIP_CACHE_TTL) {
            localStorage.removeItem(AUTOSHIP_CACHE_KEY);
            return null;
        }

        return data;
    } catch {
        localStorage.removeItem(AUTOSHIP_CACHE_KEY);
        return null;
    }
};

export const writeCache = (data: {
    ogMerchantUserId?: string;
    hasActiveSubscriptions?: boolean;
    subscriptions?: unknown[];
    count?: number;
}) => {
    try {
        const cacheData = {
            data,
            timestamp: Date.now(),
        };
        localStorage.setItem(AUTOSHIP_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
        // Ignore cache write errors silently
        console.error('Writing cache failed:', error);
    }
};
